"use client";

import TableSortingIcon from "@/components/common/TableSortingIcon";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Order, OrderStatus } from "@/types/models/order";
import { ColumnDef } from "@tanstack/react-table";
import { format } from "date-fns";
import OrderActionsCell from "@/components/orders/OrderActionsCell";
import { ItemsPopover } from "@/components/orders/ItemsPopover";
import { formatCurrency } from "@/utils/currency";

const getStatusVariant = (status?: OrderStatus | null) => {
  if (!status) return "neutral";
  switch (status) {
    case "CREATED":
      return "new";
    case "CONFIRMED":
      return "success";
    case "SHIPPING":
      return "info";
    case "RETURNING":
      return "warning";
    case "COMPLETED":
      return "delivered";
    case "CANCELLED":
      return "cancelled";
    default:
      return "neutral";
  }
};

const formatStatus = (status?: OrderStatus | null) => {
  if (!status) return "Unknown";
  switch (status) {
    case "CREATED":
      return "Created";
    case "CONFIRMED":
      return "Confirmed";
    case "SHIPPING":
      return "Shipping";
    case "RETURNING":
      return "Returning";
    case "COMPLETED":
      return "Completed";
    case "CANCELLED":
      return "Cancelled";
    default:
      return (status as string).replace(/_/g, " ");
  }
};

export const columns: ColumnDef<Order>[] = [
  {
    id: "select",
    size: 50,
    minSize: 40,
    maxSize: 60,
    header: ({ table }) => {
      const { rows } = table.getRowModel();
      const selectedRows = table.getSelectedRowModel().rows;
      const isAllSelected =
        rows.length > 0 && selectedRows.length === rows.length;
      const isSomeSelected = selectedRows.length > 0 && !isAllSelected;

      return (
        <div className="flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-200">
          <Checkbox
            checked={
              isAllSelected ? true : isSomeSelected ? "indeterminate" : false
            }
            onCheckedChange={(value) => {
              if (value) {
                table.toggleAllPageRowsSelected(true);
              } else {
                table.toggleAllPageRowsSelected(false);
              }
            }}
            aria-label="Select all"
            className="!bg-transparent border-foreground text-foreground data-[state=checked]:text-foreground"
          />
        </div>
      );
    },
    cell: ({ row }) => (
      <div className="flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-200">
        <Checkbox
          checked={row.getIsSelected()}
          onCheckedChange={(value) => row.toggleSelected(!!value)}
          aria-label="Select row"
          className="!bg-transparent border-foreground text-foreground data-[state=checked]:text-foreground"
        />
      </div>
    ),
  },
  {
    accessorKey: "order_number",
    size: 130,
    minSize: 100,
    maxSize: 160,
    header: ({ column }) => {
      return (
        <Button
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          variant="ghost"
          className="w-full flex p-0 items-center justify-between hover:!bg-transparent hover:text-inherit group"
        >
          <span className="text-base font-bold">Order No.</span>
          <div className={`transition-opacity duration-200 ${column.getIsSorted() ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'}`}>
            <TableSortingIcon sortDirection={column.getIsSorted()} />
          </div>
        </Button>
      );
    },
    cell: ({ row }) => {
      const orderNumber = row.getValue("order_number") as number;
      const date = new Date(row.original.created_at);
      const formattedDate = format(date, "MMM dd yyyy");
      return (
        <div>
          <h3 className="font-mono text-xs font-semibold">
            #{orderNumber || "N/A"}
          </h3>
          <span className="text-xs text-muted-foreground">{formattedDate}</span>
        </div>
      );
    },
  },
  {
    accessorKey: "customer",
    size: 200,
    minSize: 150,
    maxSize: 300,
    header: ({ column }) => {
      return (
        <div className="group">
          <Button
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
            variant="ghost"
            className="w-full flex p-0 items-center justify-between hover:!bg-transparent hover:text-inherit"
          >
            <span className="text-base font-bold">Customer</span>
            <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-200">
              <TableSortingIcon sortDirection={column.getIsSorted()} />
            </div>
          </Button>
        </div>
      );
    },
    cell: ({ row }) => {
      const customer = row.getValue("customer") as Order["customer"];
      const firstName = customer?.first_name || "";
      const lastName = customer?.last_name || "";
      const email = customer?.email || "";

      return (
        <div>
          <h3 className="text-xs font-semibold truncate">
            {firstName || lastName
              ? `${firstName} ${lastName}`.trim()
              : "Unknown Customer"}
          </h3>
          {email && <span className="text-xs text-gray-600 truncate block">{email}</span>}
        </div>
      );
    },
  },
  {
    accessorKey: "calculation",
    size: 100,
    minSize: 80,
    maxSize: 120,
    header: ({ column }) => {
      return (
        <div className="group">
          <Button
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
            variant="ghost"
            className="w-full flex p-0 items-center justify-between hover:!bg-transparent hover:text-inherit"
          >
            <span className="text-base font-bold">Amount</span>
            <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-200">
              <TableSortingIcon sortDirection={column.getIsSorted()} />
            </div>
          </Button>
        </div>
      );
    },
    cell: ({ row }) => {
      const calculation = row.getValue("calculation") as Order["calculation"];
      const total = calculation?.grand_total_int || 0;
      return (
        <div>
          <h3 className="text-xs font-semibold">{formatCurrency(total)}</h3>
        </div>
      );
    },
  },
  {
    accessorKey: "variants",
    size: 250,
    minSize: 180,
    maxSize: 400,
    header: () => <span className="text-base font-bold">Items</span>,
    cell: ({ row }) => {
      const variants = row.getValue("variants") as Order["variants"];
      const items = row.original.items as Order["items"];
      const calculation = row.original.calculation;
      const totalQuantity = calculation?.total_quantity || 0;
      const itemCount = variants?.length || 0;

      if (!variants || variants.length === 0) {
        return (
          <div>
            <h3 className="text-xs font-semibold">No items</h3>
            <span className="text-xs text-gray-600">0 units</span>
          </div>
        );
      }

      // Get first few variant titles to show
      const variantTitles = variants
        .slice(0, 2)
        .map((variant) => variant?.title || "Unknown")
        .join(", ");
      const hasMore = variants.length > 2;

      const triggerContent = (
        <div>
          <h3
            className="text-xs font-semibold truncate max-w-[200px]"
            title={variantTitles}
          >
            {variantTitles}
            {hasMore ? ` +${variants.length - 2} more` : ""}
          </h3>
          <span className="text-xs text-gray-600">
            {totalQuantity} unit{totalQuantity !== 1 ? "s" : ""} • {itemCount}{" "}
            variant{itemCount !== 1 ? "s" : ""}
          </span>
        </div>
      );

      return (
        <ItemsPopover
          variants={variants}
          items={items}
          trigger={triggerContent}
        />
      );
    },
  },
  {
    accessorKey: "payment_status",
    size: 120,
    minSize: 100,
    maxSize: 150,
    header: ({ column }) => {
      return (
        <Button
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          variant="ghost"
          className="w-full flex p-0 items-center justify-between hover:!bg-transparent hover:text-inherit group"
        >
          <span className="text-base font-bold">Payment</span>
          <div className={`transition-opacity duration-200 ${column.getIsSorted() ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'}`}>
            <TableSortingIcon sortDirection={column.getIsSorted()} />
          </div>
        </Button>
      );
    },
    cell: ({ row }) => {
      const paymentStatus = row.getValue("payment_status") as string;
      const paymentType = row.original.supported_payment_type?.[0];

      // Format payment status for display
      let displayStatus = "Unknown";
      switch (paymentStatus) {
        case "NULL":
          displayStatus = "Pending";
          break;
        case "CREATED":
          displayStatus = "Created";
          break;
        case "AUTHORIZED":
          displayStatus = "Authorized";
          break;
        case "CAPTURED":
          displayStatus = "Paid";
          break;
        case "FAILED":
          displayStatus = "Failed";
          break;
        case "REFUNDED":
          displayStatus = "Refunded";
          break;
        case "CANCELLED":
          displayStatus = "Cancelled";
          break;
        default:
          displayStatus = paymentStatus || "Unknown";
      }

      return (
        <div>
          <h3 className="capitalize text-xs font-semibold">{displayStatus}</h3>
          {paymentType && (
            <span className="text-xs text-gray-600 capitalize">
              {paymentType.toLowerCase()}
            </span>
          )}
        </div>
      );
    },
  },
  {
    accessorKey: "order_sets",
    size: 140,
    minSize: 120,
    maxSize: 180,
    header: ({ column }) => {
      return (
        <Button
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          variant="ghost"
          className="w-full flex p-0 items-center justify-between hover:!bg-transparent hover:text-inherit group"
        >
          <span className="text-base font-bold">Delivery</span>
          <div className={`transition-opacity duration-200 ${column.getIsSorted() ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'}`}>
            <TableSortingIcon sortDirection={column.getIsSorted()} />
          </div>
        </Button>
      );
    },
    cell: ({ row }) => {
      const orderSets = row.getValue("order_sets") as Order["order_sets"];
      const shippingAddress = row.original.shipping_address;
      const setCount = orderSets?.length || 0;
      const needsVerification =
        orderSets?.some((set) => set?.needs_verification) || false;

      return (
        <div>
          <h3 className="text-xs font-semibold">
            {setCount} shipment{setCount !== 1 ? "s" : ""}
          </h3>
          <span className="text-xs text-gray-600">
            {needsVerification
              ? "Needs verification"
              : shippingAddress?.id
                ? "Address set"
                : "No address"}
          </span>
        </div>
      );
    },
  },
  {
    accessorKey: "current_order_stage",
    size: 120,
    minSize: 100,
    maxSize: 160,
    header: () => <span className="text-base font-bold">Status</span>,
    cell: ({ row }) => {
      const status = (row.getValue("current_order_stage") as OrderStatus) || null;
      return (
        <Badge
          variant={getStatusVariant(status) as any}
        >
          {formatStatus(status)}
        </Badge>
      );
    },
  },
  {
    accessorKey: "actions",
    size: 190,
    minSize: 100,
    maxSize: 350,
    header: () => <span className="text-base font-bold">Actions</span>,
    cell: ({ row }) => <OrderActionsCell order={row.original} />,
  },
];
